import React from 'react';
import { 
  Grid, 
  Box, 
  Typography, 
  useTheme, 
  alpha,
  Card,
  Divider,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip
} from '@mui/material';
import { 
  Computer as ServerIcon,
  Public as RegionsIcon,
  DataUsage as MemoryIcon,
  Speed as CPUIcon,
  Article as EventsIcon,
  PlayArrow as ServicesIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import EsdlMetricCard from './EsdlMetricCard';

/**
 * ESDL Stats Summary Component
 * Displays summary metrics from the dashboard data
 * 
 * @param {Object} props
 * @param {Object} props.data - The dashboard summary data
 * @param {boolean} props.loading - Whether the component is in loading state
 * @param {function} props.onRegionSelect - Optional callback when a region is selected
 */
const EsdlStatsSummary = ({ data, loading = false, onRegionSelect }) => {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogType, setDialogType] = React.useState('');
  const [dialogData, setDialogData] = React.useState(null);
  const [selectedRegion, setSelectedRegion] = React.useState('');
  
  // Safely access data properties
  const getTotalHosts = () => {
    return data?.hosts?.length || 0;
  };
  
  const getHostsHealth = () => {
    // Use the centrally calculated host_counts if available
    if (data?.host_counts) {
      return {
        healthy: data.host_counts.healthy || 0,
        warning: data.host_counts.warning || 0,
        critical: data.host_counts.critical || 0
      };
    }
    
    // Legacy fallback (should not be needed with our new implementation)
    return {
      healthy: data?.system_health?.healthy || 0,
      warning: data?.system_health?.warning || 0,
      critical: data?.system_health?.critical || 0
    };
  };
  
  const getHealthPercentage = () => {
    const health = getHostsHealth();
    const total = health.healthy + health.warning + health.critical;
    if (total === 0) return 0;
    return Math.round((health.healthy / total) * 100);
  };
  
  const getRegionsCount = () => {
    if (data?.regions) {
      return Object.keys(data.regions).length;
    }
    return data?.regions_count || 0;
  };
  
  const getServicesStats = () => {
    return {
      active: data?.services?.active || 0,
      inactive: data?.services?.inactive || 0,
      total: data?.services?.total || 0,
      healthPercent: data?.services?.health_percent || 0
    };
  };
  
  const getEventsStats = () => {
    return {
      received: data?.events?.received || 0,
      processed: data?.events?.processed || 0,
      dropped: data?.events?.dropped || 0,
      efficiency: data?.events?.efficiency_percent || 0
    };
  };
  
  const getSystemHealth = () => {
    // Use the centrally calculated host_counts for critical host lists if available
    if (data?.host_counts?.criticalHosts) {
      return {
        cpu: {
          avg_load: data.system_health?.avg_cpu_load || 0,
          critical_hosts: data.host_counts.criticalHosts.cpu || []
        },
        memory: {
          avg_percent: data.system_health?.avg_memory_percent || 0,
          critical_hosts: data.host_counts.criticalHosts.memory || []
        },
        disk: {
          avg_percent: data.system_health?.avg_disk_percent || 0,
          critical_hosts: data.host_counts.criticalHosts.disk || []
        }
      };
    }

    // Safely check if the data exists with proper structure
    const isValidSystemHealth = data?.system_health && 
      (typeof data.system_health.avg_cpu_load !== 'undefined' || 
       typeof data.system_health.avg_memory_percent !== 'undefined' || 
       typeof data.system_health.avg_disk_percent !== 'undefined');
      
    // Use fallback values if no data is available
    if (!isValidSystemHealth) {
      console.warn('System health data not available, using default values');
      return {
        cpu: {
          avg_load: 0,
          critical_hosts: []
        },
        memory: {
          avg_percent: 0,
          critical_hosts: []
        },
        disk: {
          avg_percent: 0,
          critical_hosts: []
        }
      };
    }
    
    // Extract the data from the proper structure
    return {
      cpu: {
        avg_load: data.system_health.avg_cpu_load || 0,
        critical_hosts: data.system_health.critical_hosts?.cpu || []
      },
      memory: {
        avg_percent: data.system_health.avg_memory_percent || 0,
        critical_hosts: data.system_health.critical_hosts?.memory || []
      },
      disk: {
        avg_percent: data.system_health.avg_disk_percent || 0,
        critical_hosts: data.system_health.critical_hosts?.disk || []
      }
    };
  };
  
  // Helper function to get region health summary
  const getRegionHealthSummary = () => {
    if (!data?.regions) return { healthy: 0, warning: 0, critical: 0 };
    
    // Count regions with at least one host in each health status
    let healthyCounts = 0;
    let warningCounts = 0;
    let criticalCounts = 0;
    
    Object.values(data.regions).forEach(regionData => {
      if (regionData.healthy > 0) healthyCounts++;
      if (regionData.warning > 0) warningCounts++;
      if (regionData.critical > 0) criticalCounts++;
    });
    
    return {
      healthy: healthyCounts,
      warning: warningCounts,
      critical: criticalCounts
    };
  };
  
  // Format numbers to be more readable
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };
  
  // Handle opening detail dialogs
  const handleOpenDialog = (type, data) => {
    setDialogType(type);
    setDialogData(data);
    setDialogOpen(true);
  };
  
  // Handle closing detail dialogs
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  // Render different dialog content based on type
  const renderDialogContent = () => {
    switch(dialogType) {
      case 'hosts':
        return (
          <>
            <DialogTitle>
              Host Health Breakdown
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      {getHostsHealth().healthy}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Healthy Hosts
                    </Typography>
                    <SuccessIcon sx={{ fontSize: 40, color: theme.palette.success.main, mt: 1, opacity: 0.8 }} />
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="warning.main" fontWeight="bold">
                      {getHostsHealth().warning}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Warning Hosts
                    </Typography>
                    <WarningIcon sx={{ fontSize: 40, color: theme.palette.warning.main, mt: 1, opacity: 0.8 }} />
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="error.main" fontWeight="bold">
                      {getHostsHealth().critical}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Critical Hosts
                    </Typography>
                    <ErrorIcon sx={{ fontSize: 40, color: theme.palette.error.main, mt: 1, opacity: 0.8 }} />
                  </Box>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Health Status by Region
              </Typography>
              <List>
                {Object.entries(data?.regions || {}).map(([region, stats]) => (
                  <ListItem key={region} divider>
                    <ListItemText 
                      primary={region} 
                      secondary={`${stats.hosts} hosts total`} 
                    />
                    <Box display="flex" gap={1}>
                      {stats.healthy > 0 && (
                        <Chip 
                          size="small" 
                          color="success" 
                          label={`${stats.healthy} healthy`}
                          icon={<SuccessIcon />}
                        />
                      )}
                      {stats.warning > 0 && (
                        <Chip 
                          size="small" 
                          color="warning" 
                          label={`${stats.warning} warning`}
                          icon={<WarningIcon />}
                        />
                      )}
                      {stats.critical > 0 && (
                        <Chip 
                          size="small" 
                          color="error" 
                          label={`${stats.critical} critical`}
                          icon={<ErrorIcon />}
                        />
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            </DialogContent>
          </>
        );
        
      case 'services':
        return (
          <>
            <DialogTitle>
              Services Health Details
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      {getServicesStats().active}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Services
                    </Typography>
                    <SuccessIcon sx={{ fontSize: 40, color: theme.palette.success.main, mt: 1, opacity: 0.8 }} />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="error.main" fontWeight="bold">
                      {getServicesStats().inactive}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Inactive Services
                    </Typography>
                    <ErrorIcon sx={{ fontSize: 40, color: theme.palette.error.main, mt: 1, opacity: 0.8 }} />
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Service Health Percentage
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {getServicesStats().healthPercent}%
                </Typography>
                <LinearProgress 
                  variant="determinate"
                  value={getServicesStats().healthPercent}
                  color="success"
                  sx={{ height: 10, borderRadius: 5, mt: 1 }}
                />
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Service Status Summary
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Total of {getServicesStats().total} services monitored across {getTotalHosts()} hosts.
                {getServicesStats().healthPercent > 90 ? 
                  ' Overall system health is good.' : 
                  ' Some services require attention.'}
              </Typography>
            </DialogContent>
          </>
        );
        
      case 'events':
        return (
          <>
            <DialogTitle>
              Event Processing Details
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h5" color="info.main" fontWeight="bold">
                      {formatNumber(getEventsStats().received)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Received Events
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h5" color="success.main" fontWeight="bold">
                      {formatNumber(getEventsStats().processed)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Processed Events
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h5" color="error.main" fontWeight="bold">
                      {formatNumber(getEventsStats().dropped)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Dropped Events
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Processing Efficiency
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {getEventsStats().efficiency}%
                </Typography>
                <LinearProgress 
                  variant="determinate"
                  value={getEventsStats().efficiency}
                  color={getEventsStats().efficiency > 90 ? "success" : 
                         getEventsStats().efficiency > 75 ? "warning" : "error"}
                  sx={{ height: 10, borderRadius: 5, mt: 1 }}
                />
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Event Processing Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                The system has processed {formatNumber(getEventsStats().processed)} events out of
                {formatNumber(getEventsStats().received)} received, with a processing efficiency of 
                {getEventsStats().efficiency}%.
                {getEventsStats().dropped > 0 ? 
                  ` ${formatNumber(getEventsStats().dropped)} events were dropped and may require investigation.` : 
                  ' No events were dropped, indicating good system performance.'}
              </Typography>
            </DialogContent>
          </>
        );
        
      case 'cpu':
      case 'memory':
      case 'disk':
        const resourceData = {
          cpu: {
            title: 'CPU Load Details',
            avg: getSystemHealth().cpu.avg_load,
            unit: '',
            criticalHosts: getSystemHealth().cpu.critical_hosts,
            icon: <CPUIcon sx={{ fontSize: 40, color: theme.palette.primary.main, opacity: 0.8 }} />
          },
          memory: {
            title: 'Memory Usage Details',
            avg: getSystemHealth().memory.avg_percent,
            unit: '%',
            criticalHosts: getSystemHealth().memory.critical_hosts,
            icon: <MemoryIcon sx={{ fontSize: 40, color: theme.palette.info.main, opacity: 0.8 }} />
          },
          disk: {
            title: 'Disk Usage Details',
            avg: getSystemHealth().disk.avg_percent,
            unit: '%',
            criticalHosts: getSystemHealth().disk.critical_hosts,
            icon: <ServerIcon sx={{ fontSize: 40, color: theme.palette.warning.main, opacity: 0.8 }} />
          }
        }[dialogType];
        
        return (
          <>
            <DialogTitle>
              {resourceData.title}
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                {resourceData.icon}
                <Typography variant="h4" fontWeight="bold" mt={1}>
                  {resourceData.avg}{resourceData.unit}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average across all hosts
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Critical Hosts ({resourceData.criticalHosts.length || 0})
              </Typography>
              
              {resourceData.criticalHosts.length > 0 ? (
                <List>
                  {resourceData.criticalHosts.map((hostname, index) => (
                    <ListItem key={index} divider>
                      <ListItemText primary={hostname} />
                      <Chip 
                        size="small" 
                        color="error" 
                        label="Critical" 
                        icon={<ErrorIcon />}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <SuccessIcon sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No hosts with critical {dialogType} usage
                  </Typography>
                </Box>
              )}
            </DialogContent>
          </>
        );
        
      case 'regions':
        return (
          <>
            <DialogTitle>
              Region Details
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <RegionsIcon sx={{ fontSize: 40, color: theme.palette.warning.main, opacity: 0.8 }} />
                <Typography variant="h4" fontWeight="bold" mt={1}>
                  {getRegionsCount()} Regions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Geographic distribution of hosts
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Host Distribution by Region
              </Typography>
              
              {Object.keys(dialogData).length > 0 ? (
                <List>
                  {Object.entries(dialogData).map(([region, stats]) => (
                    <ListItem 
                      key={region} 
                      divider
                      sx={{
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.warning.main, 0.05),
                          transform: 'translateX(4px)'
                        },
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        if (onRegionSelect) {
                          onRegionSelect(region);
                          setSelectedRegion(region);
                          handleCloseDialog();
                        }
                      }}
                    >
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RegionsIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                            <Typography variant="body1" fontWeight="medium">{region}</Typography>
                          </Box>
                        } 
                        secondary={`${stats.hosts} hosts monitored`} 
                      />
                      <Box display="flex" gap={1}>
                        {stats.healthy > 0 && (
                          <Chip 
                            size="small" 
                            color="success" 
                            label={`${stats.healthy}`}
                            icon={<SuccessIcon />}
                            sx={{ minWidth: 80 }}
                          />
                        )}
                        {stats.warning > 0 && (
                          <Chip 
                            size="small" 
                            color="warning" 
                            label={`${stats.warning}`}
                            icon={<WarningIcon />}
                            sx={{ minWidth: 80 }}
                          />
                        )}
                        {stats.critical > 0 && (
                          <Chip 
                            size="small" 
                            color="error" 
                            label={`${stats.critical}`}
                            icon={<ErrorIcon />}
                            sx={{ minWidth: 80 }}
                          />
                        )}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <InfoIcon sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No region data available
                  </Typography>
                </Box>
              )}
              
              <Box mt={3} p={2} sx={{ 
                backgroundColor: alpha(theme.palette.warning.main, 0.05),
                borderRadius: 2,
                border: `1px dashed ${alpha(theme.palette.warning.main, 0.3)}`
              }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="warning.main">
                  Region Management
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Click on a region to view detailed information about hosts, services, and events specific to that region.
                </Typography>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={6}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        p: 1.5, 
                        textAlign: 'center',
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        color: theme.palette.primary.main,
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          transform: 'translateY(-2px)'
                        }
                      }}
                      onClick={() => {
                        if (onRegionSelect && selectedRegion) {
                          onRegionSelect(selectedRegion);
                          handleCloseDialog();
                          document.getElementById('hosts-section')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                    >
                      <ServerIcon fontSize="small" />
                      <Typography variant="caption" display="block" mt={0.5}>
                        Hosts by Region
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        p: 1.5, 
                        textAlign: 'center',
                        backgroundColor: alpha(theme.palette.error.main, 0.05),
                        color: theme.palette.error.main,
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                          transform: 'translateY(-2px)'
                        }
                      }}
                      onClick={() => {
                        if (onRegionSelect && selectedRegion) {
                          onRegionSelect(selectedRegion);
                          handleCloseDialog();
                          
                          const errorEvent = new CustomEvent('switch-to-errors-tab', { 
                            detail: { region: selectedRegion } 
                          });
                          window.dispatchEvent(errorEvent);
                        }
                      }}
                    >
                      <ErrorIcon fontSize="small" />
                      <Typography variant="caption" display="block" mt={0.5}>
                        Errors by Region
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
          </>
        );
        
      default:
        return null;
    }
  };
  
  if (loading) {
    return (
      <Box mb={3}>
        <LinearProgress />
      </Box>
    );
  }
  
  // Calculate host health stats
  const hostsHealth = getHostsHealth();
  const totalHosts = getTotalHosts();
  const healthPercent = getHealthPercentage();
  
  // Calculate services stats
  const servicesStats = getServicesStats();
  
  // Calculate events stats
  const eventsStats = getEventsStats();
  
  // Get system health data
  const systemHealth = getSystemHealth();
  
  return (
    <Box>
      <Grid container spacing={3} mb={3}>
        {/* Total Hosts Card */}
        <Grid item xs={12} sm={6} md={3}>
          <EsdlMetricCard
            title="Total Hosts"
            value={totalHosts.toString()}
            subtitle="Monitored servers"
            icon={ServerIcon}
            color="primary"
            chips={[
              { 
                label: `${hostsHealth.healthy} Healthy`, 
                color: 'success',
                icon: <SuccessIcon fontSize="small" />
              },
              hostsHealth.critical > 0 ? { 
                label: `${hostsHealth.critical} Critical`, 
                color: 'error',
                icon: <ErrorIcon fontSize="small" />
              } : null,
              hostsHealth.warning > 0 ? { 
                label: `${hostsHealth.warning} Warning`, 
                color: 'warning',
                icon: <WarningIcon fontSize="small" />
              } : null
            ].filter(Boolean)}
            loading={loading}
            onClick={() => handleOpenDialog('hosts', hostsHealth)}
          />
        </Grid>
        
        {/* Services Health Card */}
        <Grid item xs={12} sm={6} md={3}>
          <EsdlMetricCard
            title="Services Health"
            value={`${servicesStats.healthPercent}%`}
            subtitle={`${servicesStats.active} active of ${servicesStats.total} services`}
            icon={ServicesIcon}
            color="success"
            progress={servicesStats.healthPercent}
            chips={[
              { 
                label: `${servicesStats.active} Active`, 
                color: 'success',
                icon: <SuccessIcon fontSize="small" />
              },
              servicesStats.inactive > 0 ? { 
                label: `${servicesStats.inactive} Inactive`, 
                color: 'error',
                icon: <ErrorIcon fontSize="small" />
              } : null
            ].filter(Boolean)}
            loading={loading}
            onClick={() => handleOpenDialog('services', servicesStats)}
          />
        </Grid>
        
        {/* Events Processing Card */}
        <Grid item xs={12} sm={6} md={3}>
          <EsdlMetricCard
            title="Event Processing"
            value={`${eventsStats.efficiency}%`}
            subtitle={`${formatNumber(eventsStats.processed)} of ${formatNumber(eventsStats.received)} events`}
            icon={EventsIcon}
            color="info"
            progress={eventsStats.efficiency}
            chips={[
              { 
                label: `${formatNumber(eventsStats.processed)} Processed`, 
                color: 'success',
                icon: <SuccessIcon fontSize="small" />
              },
              eventsStats.dropped > 0 ? { 
                label: `${formatNumber(eventsStats.dropped)} Dropped`, 
                color: 'error',
                icon: <ErrorIcon fontSize="small" />
              } : null
            ].filter(Boolean)}
            loading={loading}
            onClick={() => handleOpenDialog('events', eventsStats)}
          />
        </Grid>
        
        {/* Regions Card */}
        <Grid item xs={12} sm={6} md={3}>
          <EsdlMetricCard
            title="Regions"
            value={getRegionsCount().toString()}
            subtitle="Geographic regions"
            icon={RegionsIcon}
            color="warning"
            loading={loading}
            onClick={() => handleOpenDialog('regions', data?.regions || {})}
            chips={[
              getRegionHealthSummary().healthy > 0 ? { 
                label: `${getRegionHealthSummary().healthy} Healthy`, 
                color: 'success',
                icon: <SuccessIcon fontSize="small" />
              } : null,
              getRegionHealthSummary().warning > 0 ? { 
                label: `${getRegionHealthSummary().warning} Warning`, 
                color: 'warning',
                icon: <WarningIcon fontSize="small" />
              } : null,
              getRegionHealthSummary().critical > 0 ? { 
                label: `${getRegionHealthSummary().critical} Critical`, 
                color: 'error',
                icon: <ErrorIcon fontSize="small" />
              } : null
            ].filter(Boolean)}
          />
        </Grid>
      </Grid>
      
      {/* Resource Health Summary */}
      <Box 
        component={Card} 
        mb={3} 
        p={2.5}
        sx={{ 
          borderRadius: 3,
          boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.06)}`,
          background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.85)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
          backdropFilter: 'blur(10px)'
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
          System Resources Health
        </Typography>
        
        <Grid container spacing={2}>
          {/* CPU Health */}
          <Grid item xs={12} sm={4}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1,
                cursor: 'pointer'
              }}
              onClick={() => handleOpenDialog('cpu', systemHealth.cpu)}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  mr: 1.5,
                  width: 36,
                  height: 36
                }}
              >
                <CPUIcon />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  CPU
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Average CPU load
                </Typography>
              </Box>
            </Box>
            
            <Tooltip title={`Average CPU load: ${systemHealth.cpu.avg_load || 0}%`}>
              <Box sx={{ cursor: 'pointer' }} onClick={() => handleOpenDialog('cpu', systemHealth.cpu)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {systemHealth.cpu.critical_hosts?.length || 0} hosts critical
                  </Typography>
                  <Typography variant="caption" fontWeight="bold">
                    {systemHealth.cpu.avg_load || 0}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate"
                  value={systemHealth.cpu.avg_load || 0}
                  color="primary"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Tooltip>
          </Grid>
          
          {/* Memory Health */}
          <Grid item xs={12} sm={4}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1,
                cursor: 'pointer'
              }}
              onClick={() => handleOpenDialog('memory', systemHealth.memory)}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.main,
                  mr: 1.5,
                  width: 36,
                  height: 36
                }}
              >
                <MemoryIcon />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Memory
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Memory utilization
                </Typography>
              </Box>
            </Box>
            
            <Tooltip title={`Average memory usage: ${systemHealth.memory.avg_percent || 0}%`}>
              <Box sx={{ cursor: 'pointer' }} onClick={() => handleOpenDialog('memory', systemHealth.memory)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {systemHealth.memory.critical_hosts?.length || 0} hosts critical
                  </Typography>
                  <Typography variant="caption" fontWeight="bold">
                    {systemHealth.memory.avg_percent || 0}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate"
                  value={systemHealth.memory.avg_percent || 0}
                  color="info"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Tooltip>
          </Grid>
          
          {/* Disk Health */}
          <Grid item xs={12} sm={4}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1,
                cursor: 'pointer'
              }}
              onClick={() => handleOpenDialog('disk', systemHealth.disk)}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  color: theme.palette.warning.main,
                  mr: 1.5,
                  width: 36,
                  height: 36
                }}
              >
                <ServerIcon />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Disk
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Storage utilization
                </Typography>
              </Box>
            </Box>
            
            <Tooltip title={`Average disk usage: ${systemHealth.disk.avg_percent || 0}%`}>
              <Box sx={{ cursor: 'pointer' }} onClick={() => handleOpenDialog('disk', systemHealth.disk)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {systemHealth.disk.critical_hosts?.length || 0} hosts critical
                  </Typography>
                  <Typography variant="caption" fontWeight="bold">
                    {systemHealth.disk.avg_percent || 0}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate"
                  value={systemHealth.disk.avg_percent || 0}
                  color="warning"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>
      
      {/* Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        {renderDialogContent()}
      </Dialog>
    </Box>
  );
};

export default EsdlStatsSummary; 